/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { renderBreakingNews, renderComponent, renderIsomorphicComponent } from "@quintype/framework/client/start";
import get from "lodash/get";
import { ExtendedLoadMore } from "../arrow-components/src/components/Atoms/ExtendedLoadMore";
import { AmazonPollyPopup } from "../isomorphic/components/amazon-polly/amazon-polly-popup";
import DFPHelper from "../isomorphic/components/atoms/dfp-helper";
import { MobileDownloadLinks } from "../isomorphic/components/atoms/mobile-apps";
import { LoadingIndicatorComponent } from "../isomorphic/components/basic/loading-indicator.js";
import { MeteredStoryIndicator } from "../isomorphic/components/basic/metered-story-indicator/metered-story-indicator";
import { BreakingNewsView } from "../isomorphic/components/breaking-news/breaking-news-view";
import WithSubsequentLoad from "../isomorphic/components/hoc-components/with-subsequent-load";
import WithArrowConfig from "../isomorphic/components/hoc-components/with-arrow-config";
import { InjectWidget } from "../isomorphic/components/inject-widget";
import { Metype } from "../isomorphic/components/molecules/metype";
import { PBThemeSettings } from "../isomorphic/components/pb-theme";
import { FooterLink } from "../isomorphic/components/rows/footer/link";
import { Menu } from "../isomorphic/components/rows/footer/menu";
import { SocialFollowWrapper } from "../isomorphic/components/rows/footer/social-follow-wrapper";
import { SSOWrapper } from "../isomorphic/components/sso-components/sso-dynamic";
import { WidgetComponent } from "../isomorphic/components/Widget";
import { pickComponent } from "../isomorphic/pick-component";
import { pickFooter } from "../isomorphic/pick-footer";
import { pickHeader } from "../isomorphic/pick-header";
import { decodeCustomCode } from "../utils/decodeCustomCode";

export function preRenderApplication(store) {
  const storeConfig = get(store.getState(), ["qt", "config"], {});
  const themeAttributes = storeConfig["theme-attributes"] || {};
  const pbConfig = storeConfig["pagebuilder-config"];
  const headerTheme = get(pbConfig, ["header", "headerStyle"], get(themeAttributes, ["header_theme"], "header_1"));
  const footerTheme = get(pbConfig, ["footer", "footerStyle"], get(themeAttributes, ["footer_theme"], "footer_1"));
  const enableAppDownloadLinks = get(pbConfig, ["footer", "enableAppDownloadLinksButton"], false);

  const isEnableLiveFeed = get(
    pbConfig,
    ["general", "meType", "enableLiveFeed"],
    get(themeAttributes, ["metype-config", "enableLiveFeed"])
  );

  pickHeader(headerTheme).then(header => renderComponent(header.default, "navbar", store));

  renderComponent(InjectWidget, "widget", store);
  renderComponent(PBThemeSettings, "pbTheme", store);
  renderComponent(FooterLink, "footer-link", store);

  if (footerTheme !== "footer_1") {
    renderComponent(SocialFollowWrapper, "social-follow-container", store, { hydrate: true });
    renderComponent(Menu, "footer-menu", store, { hydrate: true });

    if (pbConfig && enableAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "mobile-download-links", store, { hydrate: true });
    }
  }

  if (!themeAttributes.hide_breaking_news) renderBreakingNews("breaking-news-container", store, BreakingNewsView);
  if (isEnableLiveFeed) global.setTimeout(() => renderComponent(Metype, "meType", store), 3000);
}

// This is a separate file as everything from here on is hot reloaded when the app changes
export function renderApplication(store) {
  const storeQTData = get(store.getState(), ["qt"], {});
  const storeConfig = get(storeQTData, ["config"], {});
  const pageType = get(storeQTData, ["pageType"], "");
  const pbConfig = storeConfig["pagebuilder-config"];
  const lightRows = get(pbConfig, ["home", "lightRows"], false);
  const themeAttributes = storeConfig["theme-attributes"];
  const footerTheme = get(pbConfig, ["footer", "footerStyle"], get(themeAttributes, ["footer_theme"], "footer_1"));
  const enableAppDownloadLinks = get(pbConfig, ["footer", "enableAppDownloadLinksButton"], false);

  renderBreakingNews("breaking-news-container", store, BreakingNewsView);
  renderComponent(LoadingIndicatorComponent, "loading-indicator", store);
  renderComponent(DFPHelper, "dfp-helper", store);
  // TODO: Lazyload SSO
  renderComponent(SSOWrapper, "sso-wrapper", store);
  // TODO: Render only in story page.
  renderComponent(MeteredStoryIndicator, "metered-story-indicator", store);
  renderComponent(AmazonPollyPopup, "amazon-polly", store);
  renderComponent(FooterLink, "footer-link", store);

  if (footerTheme !== "footer_1") {
    renderComponent(SocialFollowWrapper, "social-follow-container", store, { hydrate: true });
    renderComponent(Menu, "footer-menu", store, { hydrate: true });

    if (pbConfig && enableAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "mobile-download-links", store, { hydrate: true });
    }
  }

  /*
    Intentional, needed here to inform webpack the client needs to get a chunk for the related footer
    DO NOT render footer post chunk load
  */
  pickFooter(footerTheme);

  if (global.location.hash === "#secret-mode") {
    const headerTheme = get(pbConfig, ["header", "headerStyle"], get(themeAttributes, ["header_theme"], "header_1"));

    pickHeader(headerTheme).then(header => renderComponent(header.default, "navbar", store));

    import(/* webpackChunkName: "secret-mode" */ "./secret/secret-mode").then(({ startSecretMode }) =>
      startSecretMode(store)
    );
  }

  if (pageType === "home-page" && lightRows) {
    global.qtLoadedFromShell && renderIsomorphicComponent("container", store, pickComponent, { hydrate: true });
  } else {
    renderIsomorphicComponent("container", store, pickComponent, { hydrate: !global.qtLoadedFromShell });
  }

  if (pageType === "home-page" && lightRows) {
    const collectionItems = get(storeQTData, ["data", "collection", "items"], []);
    const pbConfigRows = get(pbConfig, ["home", "rows"], []);

    /*
      Loop through pb config to check if the collection
      component has load more as subsequent to hydrate it
    */
    pbConfigRows
      .filter(row => row.type === "collections")
      .forEach(({ config = {}, layout }, index) => {
        const collection = collectionItems[index];
        if (collection) {
          const elementId = `ELM-${collection.id}`;
          const timeoutCounter = setTimeout(() => {
            const element = global && global.document && global.document.getElementById(elementId);
            element &&
              renderComponent(ExtendedLoadMore, elementId, store, {
                config,
                componentName: layout,
                WithArrowConfig,
                withSubsequentLoad: WithSubsequentLoad
              });
            clearTimeout(timeoutCounter);
          }, 2500);
        }
      });

    /*
      Loop through pb config to selectively hydrate widget rows
    */
    pbConfigRows
      .filter(row => row.type === "widget")
      .forEach(row => {
        renderComponent(WidgetComponent, `widget-${row.rowId}`, store, {
          widgetValue: `<div>${decodeCustomCode(get(row, ["config", "customCode"], ""))}</div>`,
          hydrate: true
        });
      });
  }
}
